import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api"; // Make sure this ends with '/api/'
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

const OfficeStocksList = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [balance, setBalance] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const navigate = useNavigate();

  // Fetch transactions and balance on mount
  useEffect(() => {
    // Fetch transactions
    fetch(`${BASE_URL}officestocks/transactions`) // Removed staff_id unless required
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 1) {
          setTransactions(data.transactions);
          setFilteredTransactions(data.transactions);
        } else {
          alert("Failed to fetch office stock transactions.");
        }
      })
      .catch((err) => {
        console.error("Error fetching office stock transactions:", err);
      });

    // Fetch balance (if your API requires staff_id, keep it; else remove)
    fetch(`${BASE_URL}officestocks/balance?staff_id=4`) // Replace 4 with dynamic value if needed
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 1) {
          setBalance(data.balance);
        } else {
          alert("Failed to fetch balance.");
        }
      })
      .catch((err) => {
        console.error("Error fetching balance:", err);
      });
  }, []);

  // Filter transactions whenever filters or search term change
  useEffect(() => {
    filterTransactions(searchTerm, startDate, endDate, transactionType);
  }, [searchTerm, startDate, endDate, transactionType, transactions]);

  // Filtering function
  const filterTransactions = (term, start, end, type) => {
    const filtered = transactions.filter((transaction) => {
      const staffName = transaction.staff_name || "";
      const transType = transaction.transaction_type || "";

      // Search filter (staff_name or transaction_type)
      const matchesSearch =
        staffName.toLowerCase().includes(term.toLowerCase()) ||
        transType.toLowerCase().includes(term.toLowerCase());

      // Date filter (compare only date part)
      const transactionDate = new Date(transaction.created_at)
        .toISOString()
        .split("T")[0];
      const matchesDateRange =
        (!start || transactionDate >= start) &&
        (!end || transactionDate <= end);

      // Transaction type filter (case-insensitive)
      const matchesType =
        !type || transType.toLowerCase() === type.toLowerCase();

      return matchesSearch && matchesDateRange && matchesType;
    });

    setFilteredTransactions(filtered);
  };

  // Navigation handlers
  const handleStock = () => navigate("/AddStockk");
  const handleOutStock = () => navigate("/OutStockk");

  // Delete transaction handler
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this transaction?")) {
      fetch(`${BASE_URL}officestocks/transaction/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === 1) {
            alert(`Transaction ${id} deleted successfully!`);
            // Update transactions and filteredTransactions after delete
            setTransactions((prev) => prev.filter((t) => t.id !== id));
          } else {
            alert("Failed to delete transaction.");
          }
        })
        .catch((err) => {
          console.error("Error deleting transaction:", err);
          alert("There was an error deleting the transaction.");
        });
    }
  };

  // Format date to DD/MM/YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Export filtered transactions to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Office Stocks Transactions", 14, 10);

    const tableData = filteredTransactions.map((t) => [
      t.staff_name || "N/A",
      t.amount || "N/A",
      t.transaction_type || "N/A",
      t.description || "N/A",
      formatDate(t.created_at),
    ]);

    doc.autoTable({
      head: [
        ["Staff Name", "Amount", "Transaction Type", "Description", "Date"],
      ],
      body: tableData,
    });

    doc.save("office_stocks_transactions.pdf");
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Office Stocks</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-4">
                    <h5>
                      Total Balance:{" "}
                      {balance !== null ? `Rs. ${balance}` : "Loading..."}
                    </h5>
                  </div>
                  <div className="col-md-2 text-end">
                    <button className="btn btn-primary" onClick={handleStock}>
                      Stock
                    </button>
                    <br />
                    <br />
                  </div>
                  <div className="col-md-2 text-end">
                    <button
                      className="btn btn-primary"
                      onClick={handleOutStock}
                    >
                      Out Stock
                    </button>
                    <br />
                    <br />
                  </div>
                  <div className="col-md-4 text-end">
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Filters */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search by staff name or type"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="transactionType" className="form-label">
                      Transaction Type
                    </label>
                    <select
                      id="transactionType"
                      className="form-control"
                      value={transactionType}
                      onChange={(e) => setTransactionType(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="incoming">Stock</option>
                      <option value="outgoing">Out Stock</option>
                    </select>
                  </div>
                </div>

                <div className="row mb-3"></div>

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>STAFF NAME</th>
                        <th>AMOUNT</th>
                        <th>TRANSACTION TYPE</th>
                        <th>DESCRIPTION</th>
                        <th>DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.length > 0 ? (
                        filteredTransactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td>{transaction.staff_name || "N/A"}</td>
                            <td>{transaction.amount || "N/A"}</td>
                            <td>{transaction.transaction_type || "N/A"}</td>
                            <td>{transaction.description || "N/A"}</td>
                            <td>{formatDate(transaction.created_at)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No transactions found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OfficeStocksList;
